import { FC, useState, useEffect } from 'react';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { X, Layers, RefreshCw, AlertTriangle, DollarSign, Check } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MergeableGroup {
  _id: string;
  name: string;
  imageUrl?: string | null;
  skuCount: number;
  buyingRate?: number;
  transportFees?: number;
  storageFees?: number;
  packageFees?: number;
  jumiaCommission?: number;
  fraisTraitement?: number;
  category?: string;
  supplier?: string;
  notes?: string;
}

interface MergeGroupsModalProps {
  groups: MergeableGroup[];
  token: string;
  onMerged: () => void;
  onClose: () => void;
}

// ─── Local GroupImage helper ──────────────────────────────────────────────────

const GroupImage: FC<{ src?: string | null; size?: number }> = ({ src, size = 40 }) => {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div
        style={{ width: size, height: size, minWidth: size }}
        className="flex items-center justify-center rounded-lg bg-muted border border-border"
      >
        <Layers className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={src} alt="Group" onError={() => setErr(true)}
      style={{ width: size, height: size, minWidth: size }}
      className="rounded-lg object-cover border border-border bg-muted"
    />
  );
};

// ─── MergeGroupsModal ─────────────────────────────────────────────────────────

export const MergeGroupsModal: FC<MergeGroupsModalProps> = ({ groups, token, onMerged, onClose }) => {
  const [survivingId, setSurvivingId] = useState(groups[0]?._id ?? '');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name:            groups[0]?.name            ?? '',
    category:        groups[0]?.category        ?? '',
    supplier:        groups[0]?.supplier        ?? '',
    notes:           groups[0]?.notes           ?? '',
    buyingRate:      groups[0]?.buyingRate      ?? 0,
    transportFees:   groups[0]?.transportFees   ?? 0,
    storageFees:     groups[0]?.storageFees     ?? 0,
    packageFees:     groups[0]?.packageFees     ?? 0,
    jumiaCommission: groups[0]?.jumiaCommission ?? 15,
    fraisTraitement: groups[0]?.fraisTraitement ?? 6,
    minStockAlert:   0,
  });

  // When surviving group changes, reset form to that group's values
  useEffect(() => {
    const g = groups.find(g => g._id === survivingId);
    if (!g) return;
    setForm({
      name:            g.name            ?? '',
      category:        g.category        ?? '',
      supplier:        g.supplier        ?? '',
      notes:           g.notes           ?? '',
      buyingRate:      g.buyingRate      ?? 0,
      transportFees:   g.transportFees   ?? 0,
      storageFees:     g.storageFees     ?? 0,
      packageFees:     g.packageFees     ?? 0,
      jumiaCommission: g.jumiaCommission ?? 15,
      fraisTraitement: g.fraisTraitement ?? 6,
      minStockAlert:   0,
    });
  }, [survivingId]); // eslint-disable-line

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const totalSkus = groups.reduce((s, g) => s + g.skuCount, 0);
  const absorbedCount = groups.length - 1;

  const handleMerge = async () => {
    if (!survivingId) return;
    setSaving(true);
    const sourceGroupIds = groups.map(g => g._id);
    try {
      await dispatch(actions.mergeGroups, {
        sourceGroupIds,
        targetGroupId: survivingId,
        groupData: form,
      }, token);
      onMerged();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-dropdown overflow-hidden flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
              <Layers className="h-4 w-4 text-brand" />
              Merge Product Groups
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {groups.length} groups → 1 · {totalSkus} total SKUs
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Groups being merged */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Groups being merged
            </p>
            <div className="space-y-2">
              {groups.map(g => (
                <div
                  key={g._id}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-xl border transition-all duration-150',
                    survivingId === g._id
                      ? 'border-brand bg-brand/5'
                      : 'border-border bg-background'
                  )}
                >
                  <GroupImage src={g.imageUrl} size={40} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{g.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {g.skuCount} SKU{g.skuCount !== 1 ? 's' : ''}
                      {g.buyingRate ? ` · ${g.buyingRate} MAD` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => setSurvivingId(g._id)}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-all',
                      survivingId === g._id
                        ? 'border-brand bg-brand text-white'
                        : 'border-border text-muted-foreground hover:border-brand hover:text-brand'
                    )}
                  >
                    {survivingId === g._id && <Check className="h-3 w-3" />}
                    {survivingId === g._id ? 'Surviving' : 'Make surviving'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-dashed border-border" />

          {/* Surviving group settings */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Surviving group settings
            </p>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-foreground">Group Name *</label>
                <input
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="Group name"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground">Category</label>
                  <input value={form.category} onChange={e => set('category', e.target.value)}
                    placeholder="e.g. Health & Beauty"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground">Supplier</label>
                  <input value={form.supplier} onChange={e => set('supplier', e.target.value)}
                    placeholder="Supplier name"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
                </div>
              </div>

              {/* Cost fields */}
              <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <DollarSign className="h-3 w-3" /> Cost per unit (MAD)
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    ['buyingRate',    'Buying Rate'],
                    ['transportFees', 'Inbound transport'],
                    ['storageFees',   'Hub delivery cost'],
                    ['packageFees',   'Packaging'],
                  ] as const).map(([key, label]) => (
                    <div key={key} className="space-y-0.5">
                      <label className="text-[10px] font-semibold text-muted-foreground">{label}</label>
                      <input type="number" min={0} step={0.1} value={(form as any)[key]}
                        onChange={e => set(key, parseFloat(e.target.value) || 0)}
                        className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Jumia fees */}
              <div className="rounded-xl border border-border bg-muted/40 p-3 space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <DollarSign className="h-3 w-3" /> Jumia Fees
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-semibold text-muted-foreground">Commission Jumia (%)</label>
                    <input type="number" min={0} max={100} step={0.1} value={form.jumiaCommission}
                      onChange={e => set('jumiaCommission', parseFloat(e.target.value) || 0)}
                      className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
                  </div>
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-semibold text-muted-foreground">Frais de traitement (DH)</label>
                    <input type="number" min={0} step={0.1} value={form.fraisTraitement}
                      onChange={e => set('fraisTraitement', parseFloat(e.target.value) || 0)}
                      className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20" />
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-foreground">Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                  rows={2} placeholder="Optional notes…"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20 resize-none" />
              </div>
            </div>
          </div>

          {/* Warning box */}
          {absorbedCount > 0 && (
            <div className="flex gap-2.5 p-3 rounded-xl border border-amber-500/30 bg-amber-500/5">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 leading-relaxed">
                The other <strong>{absorbedCount}</strong> group{absorbedCount !== 1 ? 's' : ''} will be{' '}
                permanently deleted. Their SKUs and stock counts will be moved to the surviving group.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-border shrink-0">
          <Button variant="outline" size="sm" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={saving || !form.name.trim()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white"
            onClick={handleMerge}
          >
            {saving
              ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" />
              : <Layers className="h-3.5 w-3.5 mr-1" />}
            {saving ? 'Merging…' : `Merge ${groups.length} Groups`}
          </Button>
        </div>
      </div>
    </div>
  );
};
